import { Box, Flex, IconButton, Text, useClipboard } from '@chakra-ui/react';
import { Check, Copy } from 'lucide-react';
import React from 'react';
import { PrismAsync as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface CodeBlockProps {
  code: string;
  language?: string;
}

const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const LABEL = '#9ba1ac';

// Fenced code block: header (language label + copy button) + Prism syntax highlighting.
// Always dark (oneDark) in both app themes — consistent with GitHub/ChatGPT code styling.
const CodeBlock = ({ code, language }: CodeBlockProps) => {
  const { hasCopied, onCopy } = useClipboard(code);

  return (
    <Box overflow='hidden' my={3} borderWidth='1px' borderColor='#272b33' borderRadius='lg'>
      <Flex align='center' justify='space-between' px={3} py={1.5} bg='#21252b'>
        <Text color={LABEL} fontSize='xs' fontWeight={600} textTransform='lowercase'>
          {language || 'code'}
        </Text>
        <IconButton
          color={LABEL}
          _hover={{ bg: 'whiteAlpha.200', color: '#fff' }}
          aria-label='Copy code'
          icon={hasCopied ? <Check size={14} /> : <Copy size={14} />}
          onClick={onCopy}
          size='xs'
          variant='ghost'
        />
      </Flex>
      <Box sx={{ '& pre': { margin: 0, borderRadius: 0 } }} fontSize='13px'>
        <SyntaxHighlighter
          language={language || 'text'}
          style={oneDark}
          customStyle={{
            margin: 0,
            padding: '12px 16px',
            background: '#282c34',
            fontSize: '13px',
            lineHeight: '1.6',
          }}
          codeTagProps={{ style: { fontFamily: MONO } }}
        >
          {code}
        </SyntaxHighlighter>
      </Box>
    </Box>
  );
};

export default CodeBlock;
